import { useApolloClient, useQuery } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { appLinks } from 'src/lib/routing';
import { useCreateTagLayout, useUpdateTagLayout } from 'src/modules/tagLayouts';
import type { FindTagLayoutQuery, FindTagLayoutQueryVariables } from 'types/graphql';
import PalletTagTemplateApi from '../api/PalletTagTemplate.api';
import createPalletTagStoragePlugin from './palletTagStorage';

/** The editor writes v2 pallet tags only. */
const SAVED_SCHEMA_VERSION = 2;

export default function usePalletTagStorage({
  tagConfig,
  setStorageSaveModalOpen,
  setStorageLoadModalOpen,
  setTemplateId,
}) {
  const client = useApolloClient();
  const { create: createLayout } = useCreateTagLayout('editor', { refetchQueries: [PalletTagTemplateApi.FindLayouts] });
  const { update: updateLayout } = useUpdateTagLayout('editor', { refetchQueries: [PalletTagTemplateApi.FindLayouts] });
  const navigate = useNavigate();

  const templateConfigRef = useRef<{ name: string; width: number; height: number }>(tagConfig);

  const knownLayoutIds = useRef(new Map<string, number>());

  const {
    data: mountedLayout,
    variables: mountedVariables,
    refetch: loadLayout,
  } = useQuery<FindTagLayoutQuery, FindTagLayoutQueryVariables>(PalletTagTemplateApi.FindLayout, {
    // A new tag (no `?name=`) has nothing to read, and `tagLayout(name:)` requires the name.
    skip: !templateConfigRef.current?.name,
    variables: { name: templateConfigRef.current?.name },
  });

  useEffect(() => {
    const layout = mountedLayout?.tagLayout;
    if (layout && mountedVariables?.name) knownLayoutIds.current.set(mountedVariables.name, layout.id);
  }, [mountedLayout, mountedVariables]);

  /** The id saved under this name, from the mount read or an earlier save; one network-only read the first time only. */
  const resolveLayoutId = useCallback(
    async (name: string) => {
      const known = knownLayoutIds.current.get(name);
      if (known !== undefined) return known;
      const { data: existing } = await client.query<FindTagLayoutQuery, FindTagLayoutQueryVariables>({
        query: PalletTagTemplateApi.FindLayout,
        variables: { name },
        fetchPolicy: 'network-only',
      });
      return existing.tagLayout?.id;
    },
    [client]
  );

  useEffect(() => {
    templateConfigRef.current = tagConfig;
  }, [tagConfig]);

  const plugin = useMemo(
    () =>
      createPalletTagStoragePlugin({
        setStorageSaveModalOpen,
        setStorageLoadModalOpen,
        saveRemote: async (saveState, pages, metadata) => {
          if (!templateConfigRef.current) {
            throw new Error('Document config not set');
          }

          const tagSchema = pages[0];
          const templateState = JSON.stringify(saveState);
          const schema = {
            width: `${metadata.width}in`,
            height: `${metadata.height}in`,
            layout: { html: tagSchema.html, css: tagSchema.css },
            version: SAVED_SCHEMA_VERSION,
          };

          const existingId = await resolveLayoutId(metadata.name);

          const saved =
            existingId !== undefined
              ? (await updateLayout({ id: existingId, input: { templateState, schema } })).data?.updateTagLayout
              : (
                  await createLayout({
                    input: {
                      name: metadata.name,
                      tagType: 'PALLET',
                      schemaVersion: SAVED_SCHEMA_VERSION,
                      templateState,
                      schema,
                    },
                  })
                ).data?.createTagLayout;
          if (!saved) {
            throw new Error('Tag layout was not saved');
          }
          knownLayoutIds.current.set(metadata.name, saved.id);

          void navigate(appLinks.palletTagEditor({ name: metadata.name }, { replace: true }));

          return saved;
        },
        loadRemote: async () => {
          const name = templateConfigRef.current?.name;
          if (!name) {
            return null;
          }

          const { data } = await loadLayout({ name });

          if (!data) {
            return null;
          }

          setTemplateId(data.tagLayout?.id);

          const template = data.tagLayout?.templateState;

          if (!template) {
            return null;
          }

          return JSON.parse(String(template));
        },
        tagConfig: templateConfigRef,
      }),
    [
      resolveLayoutId,
      createLayout,
      loadLayout,
      navigate,
      setTemplateId,
      setStorageSaveModalOpen,
      setStorageLoadModalOpen,
      updateLayout,
    ]
  );

  return { plugin };
}
